import hashChange from '../behaviors/hash-change'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'

import visible from '@github-ui/visible'
import {
  clearPullRequestSubmission,
  isPullRequestSubmissionContext,
  readPullRequestSubmission,
  savePullRequestSubmission,
  type PullRequestSubmissionFields,
  type PullRequestSubmissionRecovery,
  updatePullRequestSubmission,
} from '@github-ui/pull-request-recovery'

observe('meta[name="pr-submission-created"]', {
  constructor: HTMLMetaElement,
  add(meta) {
    // The server only renders this marker when pull_request_composer_submission_recovery is enabled.
    clearPullRequestSubmission(meta.content)
  },
})

function showSubmissionRecoveryError(form: HTMLFormElement) {
  const banner = form.querySelector<HTMLElement>('[data-pr-submission-error="recovery"]')
  if (banner) {
    banner.hidden = false
  }
}

function isPullRequestComposerSubmissionRecoveryEnabled(form: HTMLFormElement): boolean {
  return form.getAttribute('data-pull-request-composer-submission-recovery') === 'true'
}

function submissionRecovery(form: HTMLFormElement): PullRequestSubmissionRecovery | null {
  const id = form.getAttribute('data-pr-submission-id')
  const serialized = form.getAttribute('data-pr-submission-context')
  if (!id || !serialized) return null
  let context: unknown
  try {
    context = JSON.parse(serialized)
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error
    return null
  }
  return isPullRequestSubmissionContext(context) ? {id, context} : null
}

function updateSubmissionRecovery(form: HTMLFormElement) {
  if (!form.hasAttribute('data-pr-submission-recovery') || !isPullRequestComposerSubmissionRecoveryEnabled(form)) return
  const recovery = submissionRecovery(form)
  if (!recovery) return
  // The React-controlled title persists itself; its pre-hydration value is not recovered text.
  const title = form.querySelector<HTMLInputElement>('#pull_request_title')
  const body = form.querySelector<HTMLTextAreaElement>('#pull_request_body')
  const fields: Partial<PullRequestSubmissionFields> = {}
  if (title) fields.title = title.value
  if (body) fields.body = body.value
  updatePullRequestSubmission(recovery, fields)
}

document.addEventListener(
  'submit',
  event => {
    const form = event.target
    if (
      !(form instanceof HTMLFormElement) ||
      !form.hasAttribute('data-pr-submission-context') ||
      !isPullRequestComposerSubmissionRecoveryEnabled(form)
    )
      return
    if (form.hasAttribute('data-pr-creation-disabled')) {
      event.preventDefault()
      return
    }

    const recovery = submissionRecovery(form)
    const data = new FormData(form)
    const title = data.get('pull_request[title]')
    const body = data.get('pull_request[body]')
    if (!recovery || typeof title !== 'string' || typeof body !== 'string') {
      event.preventDefault()
      throw new Error('Invalid pull request submission recovery data')
    }
    savePullRequestSubmission(recovery, {title, body})
  },
  {capture: true},
)

on('change', '#pull_request_title, #pull_request_body', ({currentTarget: field}) => {
  if ((field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) && field.form) {
    updateSubmissionRecovery(field.form)
  }
})

window.addEventListener('pagehide', () => {
  for (const form of document.querySelectorAll<HTMLFormElement>('form[data-pr-submission-recovery]')) {
    updateSubmissionRecovery(form)
  }
})

observe('form[data-pr-submission-recovery]', {
  constructor: HTMLFormElement,
  add(form) {
    if (!isPullRequestComposerSubmissionRecoveryEnabled(form)) return
    const recovery = submissionRecovery(form)
    const submission = recovery ? readPullRequestSubmission(recovery) : null
    if (!submission) {
      showSubmissionRecoveryError(form)
      return
    }

    const title = form.querySelector<HTMLInputElement>('#pull_request_title')
    const body = form.querySelector<HTMLTextAreaElement>('#pull_request_body')
    if (title) {
      title.value = submission.title
    }
    if (body) {
      body.value = submission.body
    }
    title?.dispatchEvent(new Event('change', {bubbles: true}))
    body?.dispatchEvent(new Event('change', {bubbles: true}))
  },
  remove(form) {
    updateSubmissionRecovery(form)
  },
})

// Ordinary session-resume data must not overwrite an explicitly recovered submission.
document.addEventListener('session:resume', event => {
  if (!(event instanceof CustomEvent)) return
  const detail: unknown = event.detail
  if (typeof detail !== 'object' || detail === null || !('targetId' in detail) || typeof detail.targetId !== 'string')
    return
  const field = document.getElementById(detail.targetId)
  if (
    (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
    field.form?.hasAttribute('data-pr-submission-recovery') &&
    isPullRequestComposerSubmissionRecoveryEnabled(field.form) &&
    (field.name === 'pull_request[title]' || field.name === 'pull_request[body]')
  ) {
    event.preventDefault()
  }
})

on('click', '.js-compare-tab', function (event) {
  for (const tab of document.querySelectorAll('.js-compare-tab.selected')) {
    tab.classList.remove('selected')
  }
  event.currentTarget.classList.add('selected')

  for (const content of document.querySelectorAll('#commits_bucket, #files_bucket, #commit_comments_bucket')) {
    /* eslint-disable-next-line github/no-d-none */
    content.classList.add('d-none')
  }

  const selectedTab = event.currentTarget as HTMLAnchorElement
  const id = selectedTab.hash

  /* eslint-disable-next-line github/no-d-none, @typescript-eslint/no-non-null-assertion */
  document.querySelector<HTMLElement>(id)!.classList.remove('d-none')
  event.preventDefault()
})

// When an anchor references an element under the "Files Changed" tab
// but is not visible, switch to the tab.
//
//   /compare/foo-branch#diff-0
//   /compare/foo-branch#L1R1
hashChange(function ({target}) {
  if (!(target instanceof HTMLElement)) return

  const bucket = target.closest('#commits_bucket, #files_bucket, #commit_comments_bucket')

  if (bucket && bucket instanceof HTMLElement && !visible(bucket)) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    document.querySelector<HTMLElement>(`.js-compare-tab[href="#${bucket.id}"]`)!.click()
  }
})

// Show cross repo chooser for "compare across forks" link.
on('click', '.js-toggle-range-editor-cross-repo', function () {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  document.querySelector<HTMLElement>('.js-range-editor')!.classList.toggle('is-cross-repo')
})

observe('.js-compare-pr.open', {
  add() {
    document.body.classList.add('is-pr-composer-expanded')
  },
  remove() {
    document.body.classList.remove('is-pr-composer-expanded')
  },
})
